import React,{ useCallback, useEffect, useState, useMemo, forwardRef } from 'react';
import {
  Autocomplete,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TableSortLabel,
  Box,
  Checkbox,
  Button,
  Select,
  OutlinedInput,
  SelectChangeEvent,
  MenuItem,
} from '@mui/material';
import { StyledTableRowBinary } from 'src/shared/Styles/Styles';
import { getClimatologyCodeTables, getStations, getStationTable } from 'src/apis/getWeatherCalls';
import { useStoreContext } from 'src/store/context';
import { useAddAlert } from 'src/store/actions';
import { alertMessages, FuelModels } from 'src/constants';
import CustomTablePagination from 'src/shared/TablePagination/CustomTablePagination';
import { visuallyHidden } from '@mui/utils';
import { errorResponse, validateCharacters, ValidCharacters } from 'src/store/common';
import ClearIcon from '@mui/icons-material/Clear';
import CloseIcon from '@mui/icons-material/Close';
import Chip from '@mui/material/Chip';
import { List } from 'react-virtualized';
import '../../../download/Download.scss';
import { getFuelCatalogs } from 'src/apis/getCatalogCalls';
import { createUpdateFuelModelCatalogCall } from 'src/apis/mutationCatalogs';
import LoadingSpinner from 'src/shared/loading/loading';
import { useLocation } from 'react-router-dom';

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      width: 250,
    },
  },
};
interface RowProps {
    index:number,
    key: string,
    style: React.CSSProperties,
    data: any
  }
  
  const Row: React.FC<RowProps> = React.memo(({ index, key, style, data }) => {
    const option = data[index];
    const isSelectAll = option.value === 'select-all';
  
    // Check if it's "Select All" and if it should be checked
    const isChecked = isSelectAll
      ? data.every((opt) => opt['aria-selected'] || opt.value === 'select-all') 
      : option['aria-selected'];
  
    return (
      <li key={key} style={style} {...option}>
        <Checkbox
          id={key}
          color="success"
          checked={isChecked}
        />
        {option.value.search_name}
      </li>
    );
  });


interface Data {
  station_id: number
  station_name: string
  network_name: string
  state: string
  agency: string
}

/***
 * Following is used as an Enhanced header to filter the headers as needed
 * 
 */
interface HeadCell {
  disablePadding: boolean;
  id: keyof Data;
  label: string;
  numeric: boolean;
}

const headCells: readonly HeadCell[] = [
  {
    id: 'station_name',
    numeric: false,
    disablePadding: false,
    label: 'STATION NAME',
  },
  {
    id: 'station_id',
    numeric: false,
    disablePadding: false,
    label: 'STATION ID',
  },
  {
    id: 'network_name',
    numeric: false,
    disablePadding: false,
    label: 'NETWORK',
  },
  {
    id: 'state',
    numeric: false,
    disablePadding: false,
    label: 'STATE',
  },
  {
    id: 'agency',
    numeric: false,
    disablePadding: false,
    label: 'AGENCY OWNER',
  },
];

type Order = 'asc' | 'desc';

interface EnhancedTableProps {
  onRequestSort: (event: React.MouseEvent<unknown>, property: keyof Data) => void;
  order: Order;
  orderBy: string;
}
/***
 * Used for styling the Cells for the inner tables
 * 
 */
function EnhancedTableHead(props: Readonly <EnhancedTableProps>) {
  const { order, orderBy, onRequestSort}=
    props;

    const createSortHandler = (property: keyof Data) => (event: React.MouseEvent<unknown>) => {
      onRequestSort(event, property);
    };

  return (
    <TableHead>
      <TableRow>
        {headCells.map((headCell, index) => (
          (
            <TableCell
              className='headerParent'
              style={index ===0? {backgroundColor: '#d9d9d9', width: '30%'}: index===3?{backgroundColor: '#d9d9d9', width: '10%'}: {backgroundColor: '#d9d9d9', width: '20%'}}
              key={headCell.id}
              tabIndex={0}
              align={'left'}
              padding={headCell.disablePadding ? 'none' : 'normal'}
              sortDirection={orderBy === headCell.id ? order : false}
            >
              {
                <TableSortLabel
                  active={orderBy === headCell.id}
                  direction={orderBy === headCell.id ? order : 'asc'}
                  onClick={createSortHandler(headCell.id)}
                >
                  {headCell.label}
                  {/* Here we add the sort option + sort arrow */}
                  {orderBy === headCell.id ? (
                    <Box component="span" sx={visuallyHidden}>
                      {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                    </Box>
                  ) : <Box component="span" sx={visuallyHidden}></Box>}

                </TableSortLabel>

              }
            </TableCell>
          )
        ))}
      </TableRow>
    </TableHead>
  );
}

function StationAssignment(fuelModelCatalog) {
  const allCodeObj =  useMemo(()=>({code_key: "All",code_table_id: -1, code_value:'All', type:'All'}),[]);

  const [networkCodes, setNetworkCodes] = useState<any[]>([allCodeObj]);
  const [stateCodes, setStateCodes] = useState<any[]>([allCodeObj]);
  const [agencyOwnerCodes, setAgencyOwnerCodes] = useState<any[]>([allCodeObj]);

  const [networkSelected, setNetworkSelected] = useState<string|undefined>(networkCodes[0].code_value);
  const [stateSelected, setStateSelected] = useState<string|undefined>(stateCodes[0].code_value);
  const [agencyOwnerSelected, setAgencyOwnerSelected] = useState<string|undefined>(agencyOwnerCodes[0].code_value);

  const [order, setOrder] = useState<Order>('asc');
  const [orderBy, setOrderBy] = useState<keyof Data>('station_name');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [state, dispatch] = useStoreContext();

  const [stationTable, setStationTable] = useState<Data[]>([]);

  const addAlert = useAddAlert(dispatch);
  const [fuelCatalog, setFuelCatalog]= useState<any>(null)
  const [searchOptions, setSearchOptions] = useState<any[]>([]);

  const [selectedStationSearch, setSelectedStationSearch] = useState<any>(null)
  const [currSelectedStations, setCurrSelectedStations] = useState<any[]>([]);
  const [valsFromSearch, setValsFromSearch] = useState<any[]>([]);
  const [selectAllChecked, setSelectAllChecked] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [filteredStations, setFilteredStations] = useState<any[]>([]);
  const [stationsNotInCatalog, setStationsNotInCatalog] = useState<any[]>([]);

  const [requestToSend, setRequestToSend]= useState<any[]>([]);
  const [fuelModels, setFuelModels] = useState<string[]>([]);

  const [fuelModelsOptions, setFuelModelsOptions] = useState<string[]>(FuelModels)

  const [loading, setLoading] = useState<boolean>(false);
  const location = useLocation();
  const [stationsCallFail, setStationsCallFail] = useState<number>(0);

    /*call incase no station list is available for the search options of the stations in the catalog*/
  const stationListCallback = useCallback((varRequest:any, counter:number) =>{
      (async () => {
          try {
              const response: any = await getStations(varRequest, addAlert, state?.user);
              if (response?.stations) {
                  const stations = response?.stations.sort(function (a: any, b: any) {
                      return a.search_name.localeCompare(b.search_name);
                  })
                  setSearchOptions(stations);
                  setStationsCallFail(0)
              }
              else{
                setStationsCallFail(counter+1)
              }
          }
          catch (error) {
            console.log('error', error);
            setStationsCallFail(counter+1)
          }
      })();
  }, [addAlert, setStationsCallFail, state?.user]);

  /** Populate the drop downs for filtering the station table */
  const  climatologyCodesCallback = useCallback(() => {
    (async () => {
      try {
        const requestVars = {
          returnAll: true
        }
        const codeResponse: any = await getClimatologyCodeTables(requestVars);
        const networkCodes = codeResponse.networkId;
        const stateCodes = codeResponse.stateId;
        const agencyCodes = codeResponse.agency;

        networkCodes.unshift(allCodeObj);
        stateCodes.unshift(allCodeObj);
        agencyCodes.unshift(allCodeObj);

        setNetworkCodes(networkCodes);
        setStateCodes(stateCodes);
        setAgencyOwnerCodes(agencyCodes);
      } catch (error) {
        console.log('error', error)
        errorResponse(error, addAlert,alertMessages.retrievingStationData);
      }
    })();
  },[addAlert, allCodeObj])

  /** Make the api call and mapping for the station table */
  const stationTableCallback = useCallback((request: any, counter:number) => {
    (async () => {
      try {
        const stationTableResponse: any = await getStationTable(request, addAlert, state?.user);
        if(stationTableResponse.data.length > 0) {
          setStationTable(stationTableResponse.data);
          setTotalCount(stationTableResponse._metadata.total_count);
          setStationsCallFail(0)
        } else {
          setStationTable([]);
          setTotalCount(0);
          setStationsCallFail(counter+1)
        }
        
      } catch(error) {
        setStationTable([]);
        setTotalCount(0);
        setStationsCallFail(counter+1)
        console.log('error', error)
      }
    })();
  }, [addAlert, state?.user, setStationsCallFail]);

    /* find stations that are not in the catalog*/
  const findingStationsNotInCatalog = useCallback((allStations: any[], catalogsStations:any[])=>{
    if(!catalogsStations || (catalogsStations?.length===1 && !catalogsStations[0]?.station_id)){
      return allStations
    }
    else{
      const fuelModelStationsIdSet = new Set(catalogsStations.map(obj=>obj));
      const stationsNotCurrentlyInCatalog = allStations.filter(obj=> !fuelModelStationsIdSet.has(obj?.station_id))
      return stationsNotCurrentlyInCatalog;
    }
  },[]);

  /*populate the stations that are not in the catalog for first search*/
    const stationCallback = useCallback((stationsInCatalog: any[], counter:number) =>{
      (async()=>{
        try{
          const varRequest = {
            returnAll: true,				
          };
          const response: any = await getStations(varRequest, addAlert, state?.user);
          if(response?.stations){
            const stations = response.stations.sort(function(a:any, b:any) {
              return a.search_name.localeCompare(b.search_name);
            });

            if(stations && fuelCatalog){
              const notInCatalog = findingStationsNotInCatalog(stations, stationsInCatalog)
              setStationsNotInCatalog(notInCatalog)
            }
            setStationsCallFail(0);

          }
          else{
            setStationsCallFail(counter+1);
            setStationsNotInCatalog([]);
          }
        }
        catch(error){
          setStationsNotInCatalog([]);
          setStationsCallFail(counter+1)
          console.log('error', error)
        }
      })();
    },[addAlert, findingStationsNotInCatalog, fuelCatalog, state?.user, setStationsCallFail]);
    
  
      /** Make the api call for the fuel catalog edit */
  const fuelCatalogCallback = useCallback((request: any, fromAfterSubmit?:boolean) => {
    (async () => {
      try {
        const fuelForThisCatalogName: any = await getFuelCatalogs(request, addAlert);
        if (fuelForThisCatalogName.data.length > 0) {

          setRequestToSend(fuelForThisCatalogName.data)
          //update the current fuel model catalog's data 
          const stringArray = decodeURIComponent(location.pathname).split("/");
          const curCatalogId = Number(stringArray[stringArray?.length-1]);
          const updateCurrentFuel = fuelForThisCatalogName.data.find((catalog:any)=>catalog.catalogId===curCatalogId)
          setFuelCatalog(updateCurrentFuel);

          //In case there is a parent catalog that does not have all the fuel models 
          let newOptions = ['All'];
          fuelForThisCatalogName.data.forEach((catalog: any)=>{
            newOptions.push(catalog.fuelModel)
          })
          setFuelModelsOptions(newOptions)

          if(fromAfterSubmit){
            //one submit is clicked, we need to update the tables search bars
            const requestFor2ndSearchOptions = {
              stationId: updateCurrentFuel.stations?.join(','),
              returnAll: false,
            }
            stationListCallback(requestFor2ndSearchOptions, stationsCallFail);
            const request = {
              stationId: updateCurrentFuel.stations?.join(','),
              networkName: networkSelected,
              stateId: stateSelected,
              agency: agencyOwnerSelected,
              perPage: rowsPerPage,
              page: page,
              sortBy: orderBy,
              sortOrder: order,
            }
            stationTableCallback(request, stationsCallFail);
            stationCallback( updateCurrentFuel.stations, stationsCallFail);

          }
        }

      } catch (error) {
        console.log('error', error)
      }
    })();
  }, [addAlert, location.pathname, agencyOwnerSelected, order, orderBy, 
    page, rowsPerPage, stateSelected, stationCallback, stationListCallback, stationTableCallback,
    networkSelected, stationsCallFail
  ]);

  useEffect(()=>{
    if((requestToSend.length<1 && fuelCatalog?.catalogName) ){

      setFuelModels([fuelCatalog?.fuelModel])
      const request = {
        id: null,
        catalogName: fuelCatalog?.catalogName,
        perPage: 10000,
        page: 0,
        sortBy: 'fire_danger_model_name',
        sortOrder: 'asc',
        fuelModel: 'ALL'
      }
      fuelCatalogCallback(request)
    }
  },[requestToSend, fuelCatalog, fuelCatalogCallback, ]);

  const handleSubmit=()=>{
    const filterOutAll = fuelModels?.filter(fm => fm !== 'All');
    const filterRequestArray = requestToSend.filter(catalog =>
      filterOutAll.some(fm => catalog.fuelModel === fm)
    );
    const fuelModelRequestToSend = filterRequestArray.map((catalogToSend:any)=>{
      return {
        id: catalogToSend.catalogId,
        stations: catalogToSend.stations,
      }
    });
    //use the requestToSend
    //TO-DO handle the submit
        (async () => {
          setLoading(true)
          try {
            const response: any = await createUpdateFuelModelCatalogCall(fuelModelRequestToSend, addAlert);
            if (response?.data.data.createUpdateFireDangerModelCatalog.message?.length > 0) {
              //save banner
              const parentName = response.data.data.createUpdateFireDangerModelCatalog.data[0][0].catalog_name;
              errorResponse('', addAlert, `The Fuel Model Catalog(s), ${parentName} for ${filterOutAll.join(',')}, was saved.`, true);
              clearSelected(); 
              const request = {
                id: null,
                catalogName: parentName,
                perPage: 10000,
                page: 0,
                sortBy: 'fire_danger_model_name',
                sortOrder: 'asc',
                fuelModel: 'ALL'
              }
              fuelCatalogCallback(request, true)
            }
            setLoading(false)
          }
          catch (error: any) {
            setLoading(false);
            console.log('error', error)
            
          }
        })();
  }


  const onStationSearch = (e:React.SyntheticEvent<Element, Event>, incomingVal: any) =>{
    if(incomingVal && searchOptions?.length>0 && (fuelCatalog?.stations?.length>=1 && fuelCatalog?.stations[0])){
      const findStation = searchOptions.find((station)=>station.search_name === incomingVal?.search_name);
      setSelectedStationSearch(findStation)
      const request = {
        stationId: findStation?.station_id?.toString(),
        networkName: networkSelected,
        stateId: stateSelected,
        agency: agencyOwnerSelected,
        perPage: rowsPerPage,
        page: page,
        sortBy: orderBy,
        sortOrder: order
      }
      stationTableCallback(request, stationsCallFail);
    }
    else if(fuelCatalog?.stations?.length>=1 && fuelCatalog?.stations[0]){
      const request = {
        stationId: fuelCatalog?.stations?.join(','),
        networkName: networkSelected,
        stateId: stateSelected,
        agency: agencyOwnerSelected,
        perPage: rowsPerPage,
        page: page,
        sortBy: orderBy,
        sortOrder: order
      }
      stationTableCallback(request, stationsCallFail);
    }
  }

  const handleRequestSort = (event:React.MouseEvent<unknown>, property:keyof Data) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
    setPage(0)
    const requestSortUpdate={
      perPage: rowsPerPage,
      page: 0,
      sortBy: property,
      sortOrder: isAsc ? 'desc' : 'asc'
    }
    stationTableCallback(requestSortUpdate, stationsCallFail);
  };

  const rowRenderer = useCallback((data: any) => {
    return ({ index, key, style }) => {
      return (
        <Row index={index} key={key} style={style} data={data} />
      )
    }
  }, []);

  function ListboxComponent(props, ref) {
    const { children, selected, ...other } = props;
    const itemData = children?.map((child: any) => child.props.value);
    const allProps = children?.map((child: any) => child.props)

    return (
      <div ref={ref} role='button' tabIndex={0} onMouseDown={other.onMouseDown} >
        <List
          height={400}
          maxHeight={400}
          rowCount={itemData.length}
          rowHeight={50}
          width={775}
          rowRenderer={rowRenderer(allProps)}
          {...other}
        />

      </div>
    )
  };

  /** Check if the filter options are initialized */
  useEffect(()=>{
    if(networkCodes?.length < 2 && stateCodes?.length < 2 && agencyOwnerCodes?.length < 2) {
      climatologyCodesCallback();
    }
  },[networkCodes?.length, stateCodes?.length, agencyOwnerCodes?.length, climatologyCodesCallback]);

  /* may need to make a call to get catalog info*/
  useEffect(()=>{
    if(fuelModelCatalog?.fuelModelCatalog){
        setFuelCatalog(fuelModelCatalog.fuelModelCatalog)
    }
  },[fuelModelCatalog?.fuelModelCatalog]);

  /** Populate the stations table */
  useEffect(() => {
    if((fuelCatalog?.stations?.length>=1 && fuelCatalog?.stations[0]) ){
      const request = {
        stationId: fuelCatalog?.stations?.join(','),
        networkName: networkSelected,
        stateId: stateSelected,
        agency: agencyOwnerSelected,
        perPage: rowsPerPage,
        page: page,
        sortBy: orderBy,
        sortOrder: order
      } 
      stationTableCallback(request, stationsCallFail);
    }

  }, [ networkSelected, stateSelected, agencyOwnerSelected, stationsCallFail,
    order, orderBy, page, rowsPerPage, stationTableCallback, fuelCatalog])

  /*to populate the search bar*/
  useEffect(() => {
    if((fuelCatalog?.stations?.length>=1 && fuelCatalog?.stations[0]) ){
      const request = {
        stationId: fuelCatalog?.stations?.join(','),
        returnAll: false,
      } 
      stationListCallback(request, stationsCallFail);
    }
  }, [ stationListCallback, fuelCatalog, stationsCallFail])


    useEffect(()=>{
      if((stationsNotInCatalog?.length<1)){
        stationCallback(fuelCatalog?.stations, stationsCallFail);
      }
      
    },[stationsNotInCatalog, stationCallback, fuelCatalog?.stations, stationsCallFail]);

  const handleChangePage = (
    event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number,
  ) => {
    setPage(newPage);
    const updatePageCall = {
      perPage: rowsPerPage,
      page: newPage,
      sortBy: orderBy,
      sortOrder: order
    };
    stationTableCallback(updatePageCall, stationsCallFail);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    setRowsPerPage(Number.parseInt(event.target.value, 10));
    setPage(0);
    const updatePerPage = {
      perPage: Number.parseInt(event.target.value, 10),
      page: 0,
      sortBy: orderBy,
      sortOrder: order
    };
    stationTableCallback(updatePerPage, stationsCallFail);
  };

    /* this will clear filters*/
    const clearFilters =()=>{
      const allStationsOfcatalog = fuelCatalog?.stations?.join(',');
      setSelectedStationSearch(null)
      setNetworkSelected(networkCodes[0].code_value);
      setStateSelected(stateCodes[0].code_value)
      setAgencyOwnerSelected(agencyOwnerCodes[0].code_value);
      setPage(0);
      const request = {
        stationId: allStationsOfcatalog,
        networkName: networkCodes[0].code_value,
        stateId: stateCodes[0].code_value,
        agency: agencyOwnerCodes[0].code_value,
        perPage: rowsPerPage,
        page: 0,
        sortBy: orderBy,
        sortOrder: order
      }
      stationTableCallback(request, stationsCallFail);
    }

    const clearSelected =()=>{
      setCurrSelectedStations([]);
      setValsFromSearch([]);

    }
  const handleDelete = (station_id: string) => {
    let temp2 = currSelectedStations.filter(item => Number(item.station_id) !== Number(station_id));
    const isolatesearchName = temp2.map(value => value.search_name);
    setValsFromSearch(isolatesearchName);
    setCurrSelectedStations(temp2);

    const resultJoinedStation = temp2.map(obj => obj.station_id).join(",");

    const catalogsAndStations:any[] = requestToSend.map((catalog)=>{
      return({
        ...catalog,
        stations: resultJoinedStation
      })

    });
    setRequestToSend(catalogsAndStations);

  }

  const handleSearch = (e: React.SyntheticEvent<Element, Event>, val: any) => {
    setValsFromSearch(val);
    //TO-DO - ADD THE STATIONS TO THE requesttosend 
    if (val !== null && typeof val !== 'undefined') {
      const isolateIdsArray = val.map(value => value?.station_id);
      if (typeof isolateIdsArray[0] === 'undefined') {
        isolateIdsArray.splice(0, 1);
      }
      setCurrSelectedStations(val);
    }
    const resultJoinedStation = val.map(obj => obj.station_id).join(",");

    const catalogsAndStations:any[] = requestToSend.map((catalog)=>{
      return({
        ...catalog,
        stations: resultJoinedStation
      })

    });
    setRequestToSend(catalogsAndStations);

  };

  const handleFuelModels = (event: SelectChangeEvent) => {
    const val: any = event.target.value;
    if(val.lenth<1){
      return
    }
    let fmodels = fuelModels;
    if (val.includes('All')) {
      if (fmodels.includes('All')) {
        const filterOutAll = val.filter(fm => fm !== 'All' || fm!==fuelCatalog.fuelModel);
        if (filterOutAll.includes(fuelCatalog.fuelModel)) {
          setFuelModels(filterOutAll)

        }
        else {
          filterOutAll.push(fuelCatalog.fuelModel)
          setFuelModels(filterOutAll)
        }
      }
      else {
        setFuelModels(FuelModels);
      }

    }
    else {
      if (fmodels.includes('All')) {
        setFuelModels([fuelCatalog.fuelModel])
      } 
      else {
        setFuelModels(val)
        if(val.includes(fuelCatalog.fuelModel)){
          setFuelModels(val)

        }
        else{
          val.push(fuelCatalog.fuelModel)
          setFuelModels(val)
        }
      }
    }

  };

  const deleteFuelModels = (value: any) => {
    if(value===fuelCatalog.fuelModel){
      return
    }
    const removeSelection = fuelModels?.filter((fm) =>(fm !== value && fm !== 'All') || fm===fuelCatalog.fuelModel)
    setFuelModels(removeSelection)
  }
  return (
    <div style={{ marginLeft: '.5rem', width: '95%', height:'100%', paddingLeft: '2rem', paddingBottom: '1rem'}}>
      {(loading) && (

        <div>
          <LoadingSpinner />
        </div>
      )}
      {/* Filters Section */}
      <Box sx={{ flexShrink: 0, mb: 2 }}>
        <div
          style={{
            marginLeft: '1.5%',
            paddingTop: '2%',
            maxWidth: '100%',
          }}
        >
          <Box sx={{ boxShadow:'rgba(100, 100, 111, 0.2) 0px 7px 29px 0px', borderRadius: '5px', backgroundColor: 'white' }}>
            <div
              style={{
                margin: '1.5%',
                padding: '2%'
              }}
            >
              <div style={{marginBottom: '2rem'}}>
                <label style={{ fontSize: '1rem', marginRight: '.5rem', fontWeight: '700' }} id='fuelModels'>Fuel Model(s)
                  <Select
                    style={{ marginLeft: '1rem', width: '20rem'}}
                    multiple
                    value={fuelModels}
                    id='fuelModels'
                    input={<OutlinedInput label="Tag" />}
                    onChange={(e: any) => { handleFuelModels(e) }}
                    MenuProps={MenuProps}
                    renderValue={(selected) => (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {selected.map((value) => (
                          value !== 'All' &&
                          <Chip key={value}
                            label={value}
                            onMouseDown={(event) => {
                              event.stopPropagation();
                            }}
                            onKeyDown={(event) => { event.key === "Backspace" && deleteFuelModels(value) }}
                            onDelete={fuelCatalog?.fuelModel.includes(value) ? undefined : () => { deleteFuelModels(value) }}
                            deleteIcon={<CloseIcon tabIndex={0}
                            onKeyDown={(event) => { event.key === 'Enter' && deleteFuelModels(value) }} />}
                          />
                        ))}
                      </Box>
                    )}
                  >
                    {fuelModelsOptions.map((fm) => (
                      <MenuItem
                        key={fm}
                        value={fm}
                      >
                        {fm}
                      </MenuItem>
                    ))}
                  </Select>
                </label>
                <Button style={{ marginLeft: '45vw'}}variant="contained" type="submit" aria-disabled={valsFromSearch?.length < 1} onClick={handleSubmit}>
                  Assign Stations
                </Button>

              </div>

              <div><label className='font-sans-md text-bold' htmlFor='selectedStations'>Select Station(s) to Assign to Catalog
                
                <div className='searchStationsDownloadPage' style={{width: '50%'}}>
                  <i className="fas fa-search" style={{paddingTop: '15px'}}/>
                  <Autocomplete
                    disableCloseOnSelect
                    multiple
                    noOptionsText={
                      <div className='no-results'>
                        <div>No results match your search criteria.</div>
                        <div><b>Here are a few tips:</b></div>
                        <div>• Remember to check your spelling<br />
                          • Ensure you are searching either a Station Name or Station ID number</div>
                      </div>
                    }
                    className="dataDownload"
                    inputValue={inputValue}
                    onInputChange={(event, newInputValue, reason) => {
                      if (reason === 'input') {
                        setInputValue(newInputValue);

                        const search = newInputValue.toLowerCase();
                        const filtered = stationsNotInCatalog.filter((station) => {
                          if (search.length <= 2 && search.length !== 0) {
                            return station.state?.toLowerCase().includes(search);
                          }
                          return (
                            station.station_name?.toLowerCase().includes(search) ||
                            station.network_name?.toLowerCase().includes(search) ||
                            station.state?.toLowerCase().includes(search)
                          );
                        });

                        setFilteredStations(filtered);
                      }
                    }}
                    onChange={(e, value) => {
                      const isSelectAllCurrentlyChecked = selectAllChecked;
                      const isSelectAllStillSelected = value.some((item) => item.value === 'select-all');
                      const isFiltered = inputValue.trim().length > 0;
                      const stationsToSelect = isFiltered ? filteredStations : stationsNotInCatalog;
                      if (isSelectAllCurrentlyChecked && !isSelectAllStillSelected) {
                        handleSearch(e, []);
                        setSelectAllChecked(false);
                        return;
                      }
                      if (!isSelectAllCurrentlyChecked && isSelectAllStillSelected) {
                        const allWithSelectAll = [...stationsToSelect];
                        if (!isFiltered) {
                          allWithSelectAll.unshift({
                            station_name: 'Select All',
                            network_name: 'Select All',
                            value: 'select-all',
                            search_name: 'Select All',
                          });
                        }
                        handleSearch(e, allWithSelectAll);
                        setSelectAllChecked(!isFiltered);
                        setInputValue('');
                        setFilteredStations(stationsNotInCatalog);
                        return;
                      }
                      if (isSelectAllCurrentlyChecked && isSelectAllStillSelected) {
                        handleSearch(e, value);
                        setSelectAllChecked(false);
                        return;
                      }
                      handleSearch(e, value);
                      const allSelected = value.length === stationsNotInCatalog.length;
                      const noFilter = inputValue.trim().length === 0;
                      setSelectAllChecked(allSelected && noFilter);
                    }}
                    value={valsFromSearch}
                    id="selectedStations"
                    options={stationsNotInCatalog ? [{ station_name: 'Select All', network_name: 'Select All', value: 'select-all', search_name: 'Select All' }, ...stationsNotInCatalog] : []}
                    renderOption={
                      (props, option) => (
                        <li {...props} key={option.search_name} value={option} id={option}>
                          {option.search_name}
                        </li>
                      )}
                    isOptionEqualToValue={(option, value) => option?.search_name === value?.search_name}
                    getOptionLabel={(option) => `${option.search_name}`}
                    filterOptions={(options, { inputValue }) => {
                      const search = inputValue.toLowerCase();
                      if (search.length <= 2 && search.length !== 0) {
                        return options.filter((option) =>
                          option.state?.toLowerCase().includes(search) ||
                          option.station_name === 'Select All' ||
                          option?.station_id?.toString().includes(search)
                        )
                      }
                      return options.filter((option) =>
                        option.station_name?.toLowerCase().includes(search) ||
                        option.network_name?.toLowerCase().includes(search) ||
                        option.state?.toLowerCase().includes(search) ||
                        option?.station_name === 'Select All' ||
                        option?.station_id?.toString().includes(search)

                      )
                    }}
                    ListboxComponent={forwardRef<any, any>(ListboxComponent)}
                    renderInput={(params) => (
                      <TextField
                        placeholder='Search Stations...'
                        className="dataDownload"
                        aria-label={'Search Station...'}
                        data-testid={'search-bar'}
                        {...params}
                        InputProps={{
                          ...params.InputProps,
                          type: 'search',
                        }}

                        FormHelperTextProps={{
                          tabIndex: 0
                        }}
                      />
                    )}
                  />
                </div>
              </label>
              </div>
              {

                selectAllChecked ? (
                  <div className={valsFromSearch?.length>0?'selectedStations': 'hideRow'}>
                    <div style={{marginTop: '1.5%'}}>{`All stations have been selected. Total stations selected: ${currSelectedStations?.length - 1}`}</div>
                    <div><button type='button' onClick={clearSelected}>Clear All</button></div>

                  </div>
                ) : (
                  <div className={valsFromSearch?.length>0?'selectedStations':"hideRow"}>
                    <div style={{marginTop: '1.5%'}}>{`These are your selected stations. Total stations selected: ${currSelectedStations?.length}`}</div>
                    <div className="smooth-scrollbar" style={{ maxHeight: '400px', border: '1px solid #717171', width: '75vw', padding: '3px', borderRadius: '5px' }}>
                      {
                        currSelectedStations.map((station) =>
                          <Chip
                            label={`${station?.station_name}, ${station.network_name}, ${station.state}, ${station?.station_id}`}
                            key={station?.station_id}
                            onDelete={() => handleDelete(station?.station_id)}
                            onKeyDown={(event) => { event.key === "Backspace" && handleDelete(station?.station_id) }}
                            variant="outlined"
                            deleteIcon={<CloseIcon tabIndex={0} onKeyDown={(event) => { event.key === 'Enter' && handleDelete(station?.station_id) }} />}
                          />
                        )

                      }
                    </div>
                    <div><button type='button' onClick={clearSelected}>Clear All</button></div>
                  </div>
                )
              }
            </div>

          </Box>
          <div
            style={{paddingTop: '2%'}}
          >
            <div className="font-sans-md text-bold" style={{ display: 'flex', gap: '1rem', marginBottom: '1rem' }}>
              {/* Search Station */}
              <div style={{ width: '37%'}}>
                <label
                  htmlFor="station"
                  className="text-bold" style={{ fontSize: '18px' }}
                >
                  Search Catalog's Stations
                </label>
                <div
                  className="searchStationsDownloadPage"
                >
                  {/* Search Icons */}
                  <i
                    className="fas fa-search"
                  />
                  <Autocomplete
                    id="station"
                    isOptionEqualToValue={(option,value)=>option?.search_name===value?.search_name}
                    options={searchOptions}
                    getOptionLabel={(option)=>`${option.search_name}`}
                    value={selectedStationSearch}
                    filterOptions={(options, {inputValue})=>{
                        const search = inputValue.toLowerCase();
                        if(search.length<=2){
                            return options.filter((option)=>
                                option.state?.toLowerCase().includes(search) ||
                                option?.station_id?.toString().includes(search)

                            )
                        }
                        return options.filter((option)=>
                            option.station_name?.toLowerCase().includes(search) ||
                            option.network_name?.toLowerCase().includes(search) ||
                            option.state?.toLowerCase().includes(search)||
                            option?.station_id?.toString().includes(search)
                        )
                    }}  
                    onChange={(event, newValue) => onStationSearch(event, newValue)}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        aria-label={'Search Station...'}
                        placeholder="Search Station..."
                      />
                    )}
                  />
                </div>
              </div>
            {/* Network Filter */}
            <div style={{ width: '11%'}}>
                <label
                  htmlFor="Network"
                  className="text-bold" style={{ fontSize: '18px'}}
                >
                  Network
                </label>
                <Autocomplete
                  className='height-55'
                  disableClearable
                  id="Network"
                  options={networkCodes.map((option) => option.code_value)}
                  value={networkSelected}
                  style={{backgroundColor: 'white' }}
                  onChange={(event, newValue) =>{
                    setNetworkSelected(newValue)}
                  }
                  renderInput={(params) => (
                    <TextField 
                      onInput={(e) => validateCharacters(e, ValidCharacters.TextField)}
                      data-testid={'Network'}
                      {...params} 
                      placeholder="Select State"                   
                      InputProps={{
                      ...params.InputProps,
                      type: 'search',
                    }} />
                  )}
                />
              </div>

              {/* State Filter */}
              <div style={{ width: '11%'}}>
                <label
                  htmlFor="state"
                  className="text-bold" style={{ fontSize: '18px' }}
                >
                  State
                </label>
                <Autocomplete
                  className='height-55'
                  id="state"
                  disableClearable
                  options={stateCodes.map((option) => option.code_value)}
                  value={stateSelected}
                  style={{backgroundColor: 'white' }}

                  onChange={(event, newValue) => setStateSelected(newValue)}
                  renderInput={(params) => (
                    <TextField {...params} 
                      placeholder="Select State"
                      InputProps={{
                        ...params.InputProps,
                        type: 'search',
                      }}
                    />
                  )}
                />
              </div>

              {/* AgencyOwner Filter */}
              <div style={{ width: '11%'}}>
                <label
                  htmlFor="AgencyOwner"
                  className="text-bold" style={{ fontSize: '18px' }}
                >
                  Agency Owner
                </label>
                <Autocomplete
                  className='height-55'
                  id="AgencyOwner"
                  options={agencyOwnerCodes.map((option) => option.code_value)}
                  disableClearable
                  style={{backgroundColor: 'white' }}
                  value={agencyOwnerSelected}
                  onChange={(event, newValue) => setAgencyOwnerSelected(newValue)}
                  renderInput={(params) => (
                    <TextField {...params} 
                      placeholder="Select State"
                      InputProps={{
                        ...params.InputProps,
                        type: 'search',
                      }}
                    />
                  )}
                />
              </div>
            </div>
            <div>
              <button aria-label='Clear Filters' style={{ border: '0', backgroundColor: 'transparent', color: "#28282B", textDecoration: 'underline', cursor: 'pointer' }} type='button' onClick={clearFilters}>
                <ClearIcon style={{ fontSize: 'small' }} />Clear Filters
              </button>
            </div>
          </div>
        </div>

      {/* Table Section */}
      <Box sx={{ flexGrow: 1, overflow: 'hidden', paddingTop: '1rem' }}>

        <TableContainer
          component={Paper}
          className='margin-top-1'
          style={{
            width: '100%',
            maxWidth:'95vw',
          }}
        >
          <Table stickyHeader>
            <EnhancedTableHead
              order={order}
              orderBy={orderBy}
              onRequestSort={handleRequestSort}
            />
            <TableBody>
              {stationTable?.length<=0 && 
              <div style={{margin:'15px'}}>
                 <span className="font-sans-sm text-bold">No Stations Assigned to this Catalog</span>
              </div>}
              {stationTable?.length>=1 && stationTable.map((row, index) => (
                <StyledTableRowBinary
                  key={`${row.station_name}-${index}`}
                  hover
                  style={{
                    textAlign: 'center',
                    cursor: 'pointer',
                    border: '2px solid transparent',
                    borderRadius: '4px',
                  }}
                >
                  <TableCell className='text-align-override'>
                      {row.station_name}
                  </TableCell>
                  <TableCell className='text-align-override'>{row?.station_id}</TableCell>
                  <TableCell className='text-align-override'>{row?.network_name}</TableCell>
                  <TableCell className='text-align-override'>{row?.state}</TableCell>
                  <TableCell className='text-align-override'>{row?.agency}</TableCell>

                </StyledTableRowBinary>
              ))}
            </TableBody>
          </Table>
          <CustomTablePagination
            rowsPerPageOptions={[10, 20, 30]}
            count={totalCount}
            page={page}
            rowsPerPage={rowsPerPage}
            onPageChange={handleChangePage}
            onChangeRowsPerPage={handleChangeRowsPerPage}
          />   
        </TableContainer>
        </Box>
      </Box>
    </div>
  );
}

export default StationAssignment;